import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, Copy, Sparkles, Building2, Mail } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function OnboardWizard({ open, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    theater_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const canSubmit =
    form.theater_name.trim() && form.admin_name.trim() && form.admin_email.trim();

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/onboard", form);
      setResult(data);
      setStep(1);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onboarding failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setResult(null);
    setForm({ theater_name: "", admin_name: "", admin_email: "", admin_password: "" });
  };

  const closeAll = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" /> Onboard a new client
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-4 pt-2" data-testid="onboard-step-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 inline-flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> Create the tenant admin
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              You only need to create the client&apos;s admin account. When they sign in, StageCall will
              walk them through setting up their first production themselves.
            </p>
            <Row
              label="Theatre name"
              testid="ob-theater-name"
              value={form.theater_name}
              onChange={(v) => patch({ theater_name: v })}
              placeholder="The Willoughby Theatre"
            />
            <Row
              label="Admin full name"
              testid="ob-admin-name"
              value={form.admin_name}
              onChange={(v) => patch({ admin_name: v })}
              placeholder="Alex Chen"
            />
            <Row
              label="Admin email"
              testid="ob-admin-email"
              value={form.admin_email}
              onChange={(v) => patch({ admin_email: v })}
              placeholder="alex@willoughby.example"
              type="email"
            />
            <Row
              label="Temp password (optional — auto-generated if blank)"
              testid="ob-admin-pw"
              value={form.admin_password}
              onChange={(v) => patch({ admin_password: v })}
              placeholder="Leave blank for a strong random one"
            />
            <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3 flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div className="text-[11px] text-zinc-400 leading-relaxed">
                A welcome email with sign-in instructions and the temporary password will be sent
                to the admin automatically.
              </div>
            </div>
          </div>
        )}

        {step === 1 && result && (
          <div className="space-y-4 pt-2" data-testid="onboard-success">
            <div className="rounded-lg border border-gold/40 bg-gradient-to-br from-gold/10 to-transparent p-5 text-center">
              <Check className="w-8 h-8 text-gold mx-auto mb-2" />
              <div className="font-display text-2xl">Admin account created</div>
              <div className="text-sm text-zinc-400 mt-1">
                {result.admin.name} @ {result.admin.theater_name}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                They&apos;ll create their first production during first sign-in.
              </div>
            </div>
            <ResultRow label="Admin email" value={result.admin.email} testid="res-admin-email" />
            <ResultRow
              label="Temporary password"
              value={result.admin.temp_password}
              testid="res-admin-pw"
              mono
              warn
            />
            <ResultRow label="Sign-in link" value={result.links.login_url} testid="res-login-url" />
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              This is the only time the temporary password is shown here. A welcome email has
              already been sent to the admin — copy the password now if you need to share it out
              of band.
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 0 && (
            <>
              <button
                onClick={closeAll}
                className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                data-testid="ob-submit"
                disabled={!canSubmit || busy}
                onClick={submit}
                style={goldBtn}
                className="h-9 px-4 rounded font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create admin account
              </button>
            </>
          )}
          {step === 1 && (
            <button
              data-testid="ob-finish"
              onClick={closeAll}
              style={goldBtn}
              className="h-9 px-4 rounded font-semibold text-sm"
            >
              Done
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, testid, value, onChange, placeholder, type }) {
  return (
    <div>
      <Label className="text-zinc-300 text-xs uppercase tracking-widest">{label}</Label>
      <Input
        data-testid={testid}
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 bg-[#121212] border-white/10 h-11"
      />
    </div>
  );
}

function ResultRow({ label, value, testid, mono, warn }) {
  const copy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <div
      className={`rounded-lg border p-3 ${
        warn ? "border-amber-500/40 bg-amber-500/5" : "border-white/10 bg-[#0F0F0F]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <div
          data-testid={testid}
          className={`flex-1 min-w-0 truncate text-sm ${
            mono ? "font-mono text-gold" : "text-zinc-200"
          }`}
        >
          {value}
        </div>
        <button
          onClick={copy}
          className="p-1.5 rounded border border-white/10 text-zinc-300 hover:bg-white/5 shrink-0"
          title="Copy"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
